import React, { useState, useEffect } from 'react';
import { X, Package, DollarSign, Plus, Minus, ArrowDown, Loader2, AlertTriangle, Settings } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { updateBranchProductPrice } from '../../api/branches';
import { adjustStock, getProductStock } from '../../api/branchInventory';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Modal for editing a branch product's local price and stock
 */
const EditBranchProductModal = ({ 
  product, 
  branchId, 
  onClose, 
  onSuccess,
  formatCurrency 
}) => {
  const { user } = useAuth();
  const [localPrice, setLocalPrice] = useState(product.local_price || '');
  const [stockToAdd, setStockToAdd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingStock, setLoadingStock] = useState(true);
  const [stockInfo, setStockInfo] = useState({
    branchStock: product.branch_stock || 0,
    globalStock: 0,
    remainingStock: 0
  });
  
  // Reduce Stock State
  const [stockMode, setStockMode] = useState('add'); // 'add' or 'reduce'
  const [reduceQuantity, setReduceQuantity] = useState('');
  const [reduceReason, setReduceReason] = useState('');

  const canAdjustStock = ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role);

  useEffect(() => {
    fetchStockInfo();
  }, []);

  // Reset reduce quantity when switching modes
  useEffect(() => {
    setReduceQuantity('');
  }, [stockMode]);

  const fetchStockInfo = async () => {
    try {
      setLoadingStock(true);
      const stockData = await getProductStock(branchId, product.product_id);
      const globalRemaining = (product.global_stock || 0) - (stockData.data?.total_branch_stock || 0);
      
      setStockInfo({
        branchStock: stockData.data?.stock_quantity || product.branch_stock || 0,
        globalStock: product.global_stock || 0,
        remainingStock: Math.max(0, globalRemaining)
      });
    } catch (error) {
      console.error('Error fetching stock info:', error);
      setStockInfo({
        branchStock: product.branch_stock || 0,
        globalStock: product.global_stock || 0,
        remainingStock: 0
      });
    } finally {
      setLoadingStock(false);
    }
  };

  const handleSavePrice = async () => {
    setSubmitting(true);
    try {
      const priceValue = localPrice === '' ? null : parseFloat(localPrice);
      await updateBranchProductPrice(branchId, product.product_id, priceValue);
      toast.success('Local price updated');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update price');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddStock = async (quantity) => {
    if (!quantity || quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    setSubmitting(true);
    try {
      await adjustStock(branchId, product.product_id, quantity, 'Stock added from branch product management');
      toast.success(`Added ${quantity} units to stock`);
      
      setStockInfo(prev => ({
        ...prev,
        branchStock: prev.branchStock + quantity,
        remainingStock: Math.max(0, prev.remainingStock - quantity)
      }));
      setStockToAdd('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReduceStock = async () => {
    const qty = parseInt(reduceQuantity);
    
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity to reduce');
      return;
    }

    if (qty > stockInfo.branchStock) {
      toast.error(`Cannot reduce more than available stock (${stockInfo.branchStock})`);
      return;
    }

    if (!reduceReason.trim()) {
      toast.error('Please provide a reason for the reduction');
      return;
    }

    setSubmitting(true);
    try {
      // Call adjustStock with NEGATIVE quantity to reduce
      await adjustStock(branchId, product.product_id, -qty, reduceReason.trim());
      
      toast.success(`Reduced ${qty} units from stock`);
      setStockInfo(prev => ({ ...prev, branchStock: prev.branchStock - qty }));
      setReduceQuantity('');
      setReduceReason('');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reduce stock');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                <Package className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{product.product_name}</h2>
                <p className="text-sm text-gray-300">{product.product_code}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-6 overflow-y-auto flex-1">
          {/* Price Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Price Settings
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Global Price</label>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(product.global_price)}</p>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Effective Price</label>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(localPrice || product.global_price)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-xs text-gray-500 block mb-1">Local Price (leave empty to use global)</label>
                <input
                  type="number"
                  value={localPrice}
                  onChange={(e) => setLocalPrice(e.target.value)}
                  placeholder="Enter local price..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-accent-gold"
                  min="0"
                  step="0.01"
                />
              </div>
              <button
                onClick={handleSavePrice}
                disabled={submitting}
                className="mt-5 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Stock Section */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                Stock Management
              </h3>
              {canAdjustStock && (
                <div className="flex bg-white rounded-lg p-1 shadow-sm">
                  <button
                    onClick={() => setStockMode('add')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      stockMode === 'add' 
                        ? 'bg-blue-600 text-white' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setStockMode('reduce')}
                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                      stockMode === 'reduce' 
                        ? 'bg-red-600 text-white' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Reduce
                  </button>
                </div>
              )}
            </div>
            
            {loadingStock ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Current Branch Stock</p>
                    <p className={`text-3xl font-bold ${stockInfo.branchStock > 0 ? 'text-blue-600' : 'text-red-500'}`}>
                      {stockInfo.branchStock}
                    </p>
                  </div>
                </div>

                {/* Add Stock Mode */}
                {stockMode === 'add' && (
                  <div className="space-y-3">
                    <label className="text-xs text-gray-600 block">Add Stock to this Branch</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={stockToAdd}
                        onChange={(e) => setStockToAdd(e.target.value)}
                        placeholder="Quantity to add..."
                        className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                      <button
                        onClick={() => handleAddStock(parseInt(stockToAdd))}
                        disabled={submitting || !stockToAdd}
                        className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {/* Reduce Stock Mode */}
                {stockMode === 'reduce' && canAdjustStock && (
                  <div className="space-y-3">
                    <label className="text-xs text-gray-600 block">Quantity to Reduce</label>
                    
                    <input
                      type="number"
                      value={reduceQuantity}
                      onChange={(e) => setReduceQuantity(e.target.value)}
                      placeholder="Enter quantity to reduce..."
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      min="1"
                      max={stockInfo.branchStock}
                    />

                    {reduceQuantity && parseInt(reduceQuantity) > 0 && (
                      <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                        <div className="flex items-center gap-2 text-sm">
                          <Minus className="w-4 h-4 text-red-600" />
                          <span className="text-red-700">
                            -{reduceQuantity} units (New stock: {stockInfo.branchStock - parseInt(reduceQuantity)})
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Reason input */}
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Reason for Reduction *</label>
                      <textarea
                        value={reduceReason}
                        onChange={(e) => setReduceReason(e.target.value)}
                        rows={2}
                        placeholder="Explain why this reduction is needed..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 resize-none text-sm"
                      />
                    </div>

                    {/* Warning */}
                    <div className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-700">
                        This will reduce stock and create an audit record.
                      </p>
                    </div>

                    {/* Reduce button */}
                    <button
                      onClick={handleReduceStock}
                      disabled={submitting || !reduceQuantity || parseInt(reduceQuantity) <= 0 || !reduceReason.trim()}
                      className="w-full px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Minus className="w-4 h-4" />
                      {submitting ? 'Reducing...' : 'Confirm Reduction'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBranchProductModal;

